import { useEffect, useState } from 'react';
import { buildLeadPayload, generateEventId, propertyCategory, trackLead } from '../utils/tracking';

interface ContactForm {
  name: string;
  phone1: string;
  phone2: string;
}

/**
 * 상담 신청 폼 관리 Hook
 * - 저장 성공 응답 뒤 광고 전환 Lead (utils/tracking) — 폼 POST 와 같은 event_id 로 서버 CAPI 중복 제거
 * - propertyType: '오피스텔' → content_category 'officetel', 그 외 'apartment'
 */
export const useContactForm = (propertyId: number | undefined, propertyName: string, propertyType?: string | null) => {
  const [contactForm, setContactForm] = useState<ContactForm>({
    name: '',
    phone1: '',
    phone2: '',
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  // v2 S6 상담 접수 완료 화면용 (2026-09-22 v2h) — 성공 시 alert 대신 화면 전환
  // 2026-10-05: 완료 결과를 '그 현장'에 묶는다 — A 접수 완료가 B 상세에서 완료로 보이지 않게 (Codex 검토)
  const [submittedFor, setSubmittedFor] = useState<{ propertyId: number; phone: string } | null>(null);
  const submittedPhone = submittedFor && submittedFor.propertyId === propertyId ? submittedFor.phone : null;

  // 다른 현장으로 바뀌면 입력 중이던 폼을 비운다
  useEffect(() => {
    setContactForm({ name: '', phone1: '', phone2: '' });
  }, [propertyId]);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!propertyId) return;

    if (!contactForm.name || !contactForm.phone1 || !contactForm.phone2) {
      alert('이름과 연락처를 모두 입력해주세요.');
      return;
    }

    const fullPhone = `010-${contactForm.phone1}-${contactForm.phone2}`;

    // 광고 추적 필드 (event_id·_fbp/_fbc·페이지 URL·첫 진입 utm — 개인정보 없음)
    const eventId = generateEventId();
    const trackingPayload = buildLeadPayload(eventId);

    try {
      setFormSubmitting(true);

      const apiUrl = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : '';
      const response = await fetch(
        `${apiUrl}/api/properties/${propertyId}/contact/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            property_name: propertyName,
            customer_name: contactForm.name,
            customer_phone: fullPhone,
            email_to: 'lwkmlb@naver.com',
            ...trackingPayload,
          }),
        }
      );

      if (response.ok) {
        trackLead({ id: propertyId, name: propertyName, category: propertyCategory(propertyType) }, eventId);
        setSubmittedFor({ propertyId, phone: fullPhone });   // 보낸 현장 기준(늦은 응답도 그 현장에만)
        setContactForm({ name: '', phone1: '', phone2: '' });
      } else {
        throw new Error('Failed to submit contact form');
      }
    } catch (error) {
      console.error('Failed to submit contact:', error);
      alert('상담 신청 중 오류가 발생했습니다.\n잠시 후 다시 시도해주세요.');
    } finally {
      setFormSubmitting(false);
    }
  };

  return {
    contactForm,
    setContactForm,
    formSubmitting,
    handleContactSubmit,
    submitted: submittedPhone !== null,
    submittedPhone,
  };
};
